import {
  ButtonBase,
  Fade,
  IconButton,
  Paper,
  Typography
} from '@material-ui/core'
import LeftArrowIcon from '@material-ui/icons/ChevronLeft'
import RightArrowIcon from '@material-ui/icons/ChevronRight'
import DoubleArrowIcon from '@material-ui/icons/DoubleArrow'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { DateRangePicker } from '../../components/DatePicker/DateRangePicker'
import {
  DateObject,
  addDaysToDate,
  formatDateFullObject,
  getDatesList,
  getTodayDate,
  isWeekend
} from '../../helpers/date'
import { lang } from '../../localization'
import useStyles from './styles/ExchangeRateDateRow.Styles'

interface ExchangeRateDateRowProps {
  selectedDateInDatePicker: DateObject
  setselectedDateInDatePicker: React.Dispatch<React.SetStateAction<DateObject>>
  range: 'TWO_WEEKS'
}

export default function ExchangeRateDateRow({
  selectedDateInDatePicker,
  setselectedDateInDatePicker,
  range
}: ExchangeRateDateRowProps) {
  const classes = useStyles()
  const [isOpen, setisOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const perPage = useMemo(() => (range === 'TWO_WEEKS' ? 14 : 31), [range])
  const datesList = useMemo(
    () => getDatesList(perPage, selectedDateInDatePicker.gregorianStartDate),
    [perPage, selectedDateInDatePicker.gregorianStartDate]
  )

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef?.current?.contains(event.target as Node) &&
        isOpen
      ) {
        setisOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [wrapperRef, isOpen])

  return (
    <Paper className={classes.paperWeekly} elevation={5}>
      <div className={classes.mainBigDate}>
        <IconButton
          size="small"
          disabled={
            selectedDateInDatePicker.gregorianStartDate === getTodayDate()
          }
          onClick={() => {
            const newFirstDate = addDaysToDate(
              selectedDateInDatePicker.gregorianStartDate!,
              -perPage
            )
            const today = getTodayDate()
            const newGregorianStartDate =
              newFirstDate > today ? newFirstDate : today
            setselectedDateInDatePicker({
              ...selectedDateInDatePicker,
              gregorianStartDate: newGregorianStartDate,
              gregorianEndDate: addDaysToDate(newGregorianStartDate, 13)
            })
          }}>
          <DoubleArrowIcon
            className={[classes.prevPageIcon, classes.arrowIconLeft].join(' ')}
            fontSize="small"
          />
        </IconButton>
        <IconButton
          size="small"
          disabled={
            selectedDateInDatePicker.gregorianStartDate === getTodayDate()
          }
          onClick={() =>
            setselectedDateInDatePicker({
              ...selectedDateInDatePicker,
              gregorianStartDate: addDaysToDate(
                selectedDateInDatePicker.gregorianStartDate!,
                -1
              ),
              gregorianEndDate: addDaysToDate(
                selectedDateInDatePicker.gregorianEndDate!,
                -1
              )
            })
          }>
          <LeftArrowIcon fontSize="small" className={classes.arrowIcon} />
        </IconButton>
        {datesList && datesList.length > 0 && (
          <div className={classes.bigDateContainer} ref={wrapperRef}>
            <ButtonBase onClick={() => setisOpen(!isOpen)}>
              <Typography
                variant="h3"
                color="primary"
                className={classes.bigDate}>
                {formatDateFullObject(datesList[0])}
              </Typography>
            </ButtonBase>
            {isOpen && (
              <Fade in={isOpen}>
                <div className={classes.datePickerContainer}>
                  <DateRangePicker
                    onChange={(d: DateObject) => setselectedDateInDatePicker(d)}
                    dateObject={selectedDateInDatePicker}
                    autoSelectedEndDate
                    numberOfHoveredItems={perPage}
                    type={lang === 'fa' ? 'jalaali' : 'gregorian'}
                    onDoneClicked={() => {
                      setisOpen(false)
                    }}
                  />
                </div>
              </Fade>
            )}
          </div>
        )}
        <IconButton
          size="small"
          onClick={() =>
            setselectedDateInDatePicker({
              ...selectedDateInDatePicker,
              gregorianStartDate: addDaysToDate(
                selectedDateInDatePicker.gregorianStartDate!,
                1
              ),
              gregorianEndDate: addDaysToDate(
                selectedDateInDatePicker.gregorianEndDate!,
                1
              )
            })
          }>
          <RightArrowIcon fontSize="small" className={classes.arrowIcon} />
        </IconButton>
        <IconButton
          size="small"
          onClick={() =>
            setselectedDateInDatePicker({
              ...selectedDateInDatePicker,
              gregorianStartDate: addDaysToDate(
                selectedDateInDatePicker.gregorianStartDate!,
                perPage
              ),
              gregorianEndDate: addDaysToDate(
                selectedDateInDatePicker.gregorianEndDate!,
                perPage
              )
            })
          }>
          <DoubleArrowIcon fontSize="small" className={classes.arrowIcon} />
        </IconButton>
      </div>
      {datesList.map((item) => (
        <div
          className={[
            classes.dayContainer,
            isWeekend(item) ? classes.weekendDayContainer : '',
            lang === 'fa'
              ? classes.dayContainerSmallFa
              : classes.dayContainerSmallEn
          ].join(' ')}
          key={item.gregorianDate.date}>
          <span>
            {item[lang === 'fa' ? 'jalaaliDate' : 'gregorianDate'].shortMonth}
          </span>
          <Typography variant="h4">
            {item[lang === 'fa' ? 'jalaaliDate' : 'gregorianDate'].monthDay}
          </Typography>
          <span>{item.gregorianDate.weekDay}</span>
        </div>
      ))}
    </Paper>
  )
}
